import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { deleteGoal, fetchGoals, saveGoal } from './repository'
import type { Goal } from './types'

const GOALS_KEY = ['goals'] as const

export function useGoals() {
  return useQuery({
    queryKey: GOALS_KEY,
    queryFn: fetchGoals,
    staleTime: 60_000,
  })
}

export function useUpsertGoal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (goal: Goal) => saveGoal(goal),
    onMutate: async (goal) => {
      await qc.cancelQueries({ queryKey: GOALS_KEY })
      const previous = qc.getQueryData<Goal[]>(GOALS_KEY)
      qc.setQueryData<Goal[]>(GOALS_KEY, (prev) => {
        const list = prev ?? []
        const idx = list.findIndex((g) => g.id === goal.id)
        if (idx >= 0) return list.map((g, i) => (i === idx ? goal : g))
        return [...list, goal]
      })
      return { previous }
    },
    onError: (_err, _goal, ctx) => {
      if (ctx?.previous) qc.setQueryData(GOALS_KEY, ctx.previous)
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: GOALS_KEY })
    },
  })
}

export function useDeleteGoal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (goalId: string) => deleteGoal(goalId),
    onMutate: async (goalId) => {
      await qc.cancelQueries({ queryKey: GOALS_KEY })
      const previous = qc.getQueryData<Goal[]>(GOALS_KEY)
      qc.setQueryData<Goal[]>(GOALS_KEY, (prev) => (prev ?? []).filter((g) => g.id !== goalId))
      return { previous }
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData(GOALS_KEY, ctx.previous)
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: GOALS_KEY })
    },
  })
}
